import { cn } from '@/lib/utils'
import { SLAStatus } from '@/types'
import { SEMANTIC } from '@/lib/theme'

interface SLAIndicatorProps {
  status: SLAStatus
  timeRemaining?: number | null
  percentUsed?: number | null
  label?: string
  showTime?: boolean
  size?: 'sm' | 'md'
  className?: string
}

/**
 * Format hours into a human-readable duration string
 */
function formatDuration(hours: number): string {
  if (hours <= 0) return '0m'

  const totalMinutes = Math.round(hours * 60)
  const days = Math.floor(totalMinutes / (24 * 60))
  const remainingHours = Math.floor((totalMinutes % (24 * 60)) / 60)
  const minutes = totalMinutes % 60

  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (remainingHours > 0) parts.push(`${remainingHours}h`)
  if (minutes > 0 && days === 0) parts.push(`${minutes}m`)

  return parts.join(' ') || '0m'
}

/**
 * Get accessible description for screen readers
 */
function getAriaLabel(status: SLAStatus, timeRemaining?: number | null, label?: string): string {
  const prefix = label ? `${label}: ` : ''

  switch (status) {
    case 'met':
      return `${prefix}SLA met`
    case 'on_track':
      return timeRemaining != null
        ? `${prefix}SLA on track, ${formatDuration(timeRemaining)} remaining`
        : `${prefix}SLA on track`
    case 'warning':
      return timeRemaining != null
        ? `${prefix}SLA warning, ${formatDuration(timeRemaining)} remaining`
        : `${prefix}SLA warning`
    case 'breached':
      return `${prefix}SLA breached`
    default:
      return `${prefix}SLA status unknown`
  }
}

/**
 * Get status label text
 */
function getStatusLabel(status: SLAStatus): string {
  switch (status) {
    case 'met':
      return 'Met'
    case 'on_track':
      return 'On Track'
    case 'warning':
      return 'Warning'
    case 'breached':
      return 'Breached'
    default:
      return 'Unknown'
  }
}

/**
 * SLA Indicator Badge Component
 *
 * Displays SLA status with visual color coding and optional time remaining.
 * WCAG 2.2 AA compliant with:
 * - Color is not the only indicator (text labels accompany colors)
 * - Proper aria-labels for screen readers
 * - Sufficient color contrast (using SEMANTIC palette — Pistachio fill + Prussian text
 *   for success, Pollen fill + Prussian text for warning, Raspberry fill + white text
 *   for error)
 */
export function SLAIndicator({
  status,
  timeRemaining,
  percentUsed: _percentUsed,
  label,
  showTime = true,
  size = 'md',
  className,
}: SLAIndicatorProps) {
  const ariaLabel = getAriaLabel(status, timeRemaining, label)
  const statusLabel = getStatusLabel(status)

  // Map SLA status to SEMANTIC colors (inline styles for brand compliance)
  const getStatusColors = (slaStatus: SLAStatus) => {
    switch (slaStatus) {
      case 'met':
      case 'on_track':
        return { backgroundColor: SEMANTIC.success.fill, color: SEMANTIC.success.text }
      case 'warning':
        return { backgroundColor: SEMANTIC.warning.fill, color: SEMANTIC.warning.text }
      case 'breached':
        return { backgroundColor: SEMANTIC.error.fill, color: SEMANTIC.error.text }
      default:
        return { backgroundColor: SEMANTIC.neutral.fill, color: SEMANTIC.neutral.text }
    }
  }

  const statusColors = getStatusColors(status)

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
  }

  // Show time remaining for on_track/warning, or "Breached" for breached
  const displayText =
    status === 'breached'
      ? statusLabel
      : status === 'met'
        ? statusLabel
        : showTime && timeRemaining != null
          ? formatDuration(timeRemaining)
          : statusLabel

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      style={statusColors}
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        sizeClasses[size],
        className
      )}
    >
      {/* Status icon */}
      {status === 'met' && (
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
      {status === 'breached' && (
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      )}
      {status === 'warning' && (
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      )}

      {/* Text label */}
      <span>{displayText}</span>
    </span>
  )
}

/**
 * SLA Metrics Display Component
 *
 * Shows both response and resolution SLA status in a compact format
 */
interface SLAMetricsProps {
  responseSlaHours?: number
  resolutionSlaHours?: number
  responseStatus?: SLAStatus
  resolutionStatus?: SLAStatus
  responseTimeRemaining?: number | null
  resolutionTimeRemaining?: number | null
  responsePercentUsed?: number | null
  resolutionPercentUsed?: number | null
  className?: string
}

export function SLAMetrics({
  responseSlaHours,
  resolutionSlaHours,
  responseStatus = 'on_track',
  resolutionStatus = 'on_track',
  responseTimeRemaining,
  resolutionTimeRemaining,
  responsePercentUsed: _responsePercentUsed,
  resolutionPercentUsed: _resolutionPercentUsed,
  className,
}: SLAMetricsProps) {
  return (
    <div className={cn('space-y-3', className)}>
      {/* Response SLA */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Response:</span>
          <SLAIndicator
            status={responseStatus}
            timeRemaining={responseTimeRemaining}
            label="Response SLA"
            size="sm"
          />
        </div>
        {responseSlaHours && (
          <span className="text-xs text-muted-foreground">Target: {responseSlaHours}h</span>
        )}
      </div>

      {/* Resolution SLA */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Resolution:</span>
          <SLAIndicator
            status={resolutionStatus}
            timeRemaining={resolutionTimeRemaining}
            label="Resolution SLA"
            size="sm"
          />
        </div>
        {resolutionSlaHours && (
          <span className="text-xs text-muted-foreground">Target: {resolutionSlaHours}h</span>
        )}
      </div>
    </div>
  )
}
